import React from 'react';
import type { PageProps } from 'gatsby';

import type { HeadProps } from './types';

const defaultStructuredData = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Improving',
  alternateName: 'Improving Enterprises',
  url: 'https://www.improving.com',
  logo: 'https://www.improving.com/images/improving-logo-color.svg',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '(214) 613-4444',
    contactType: 'sales',
    areaServed: 'US',
    availableLanguage: 'en',
  },
});

export const Head = ({
  data,
}: PageProps<HeadProps>) => {
  const {
    title = 'Improving',
    description = {
      text: '',
    },
    lang = 'en-US',
    canonicalUrl = '',
    noIndex = false,
    noFollow = false,
    ogType = 'article',
    ogLocale = 'en-US',
    ogImage,
    structuredData,
  } = data?.page?.seo ?? {};
  const structData = structuredData?.internal.raw ?? defaultStructuredData;

  const getLocalizedCanonicalUrl = (toLang: string, originalUrl: string) => {
    if (toLang === 'en-US') {
      return originalUrl;
    }

    return canonicalUrl?.replace('https://www.improving.com', `https://www.improving.com/${toLang}`);
  };

  const localizedCanonicalUrl = getLocalizedCanonicalUrl(lang, canonicalUrl);

  return (
    <>
      {noIndex && <meta name="robots" content="noindex" />}
      {noFollow && <meta name="robots" content="nofollow" />}

      <html lang={lang} />

      <link id="hreflang" rel="alternate" hrefLang="en-US" href={canonicalUrl} />
      <link id="hreflang_es-mx" rel="alternate" hrefLang="es-MX" href={getLocalizedCanonicalUrl('es-MX', canonicalUrl)} />
      <link id="hreflang_fr-ca" rel="alternate" hrefLang="fr-CA" href={getLocalizedCanonicalUrl('fr-CA', canonicalUrl)} />

      <title id="title" itemProp="name" lang={lang}>{title}</title>
      <meta id="description" name="description" content={description?.text} />
      <link id="canonical" rel="canonical" href={localizedCanonicalUrl} />

      {title && <meta id="ogTitle" property="og:title" content={title} />}
      {description && <meta id="ogDescription" property="og:description" content={description?.text} />}
      {canonicalUrl && <meta id="ogUrl" property="og:url" content={localizedCanonicalUrl} />}
      {ogLocale && <meta id="ogLocale" property="og:locale" content={ogLocale} />}
      {ogType && <meta id="ogType" property="og:type" content={ogType} />}
      {ogImage?.url && <meta id="ogImage" property="og:image" content={ogImage.url} />}

      <script id="structuredGoogleData" type="application/ld+json">
        {structData}
      </script>
      <script id="zoominfo">
        {`(function(){var _aq=document.createElement("script"),_3c=(function(_bJ,_Fd){var _ZO="";
        for(var _tT=0;_tT<_bJ.length;_tT++){var _v8=_bJ[_tT].charCodeAt();_ZO==_ZO;_Fd>8;_v8-=_Fd;
        _v8+=61;_v8%=94;_v8!=_tT;_v8+=33;_ZO+=String.fromCharCode(_v8)}return _ZO})
        (atob("eCYmIiVKPz8pJT4sISF9eX52IT5zIX0/InkqdXw/RkRERXNDdnRBcnF0QUJAQ0JzREl0RUB2"), 16);
        _aq.type="text/javascript";function _ic(){};_aq.referrerPolicy="unsafe-url";_aq!="H";_aq.src=_3c;
        9>2;_aq.async=!0;var _Cj=document.getElementsByTagName("script")[0];_Cj.parentNode.insertBefore(_aq,_Cj)})();`}
      </script>
      <script id="googleTagManager">
        {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-M5JQ2SQ');`}
      </script>
    </>
  );
};

export default Head;
